import { Switch } from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { FRAME_LABELS, HOLIDAY_THEMES, getHolidayTheme, holidayGreeting } from "../../../lib/holidays";
import { HolidayIcon } from "../../../components/landing/HolidayDecor";

export default function HolidayCard({ settings, patchSettings, Card }) {
  const enabled = settings.holidayEnabled === true;
  const theme = getHolidayTheme(settings.holidayTheme);
  return (
    <Card testid="settings-holiday-card">
      <h3 className="font-serif text-xl text-stone-900">Holiday theme</h3>
      <p className="mt-1 text-sm text-stone-500">Dress up the homepage with a festive banner, floating decorations and matching paw prints. The rest of the design stays exactly the same. Turn it off to go back to the original look.</p>
      <label className="mt-5 flex items-center justify-between gap-4 text-sm font-medium text-stone-700">
        <span>Holiday theme on the homepage<span className="block text-xs font-normal text-stone-400">{enabled ? `Showing: ${theme.name}` : "Off: original Pawtastic Cuts look"}</span></span>
        <Switch checked={enabled} onCheckedChange={(v) => patchSettings({ holidayEnabled: v })} data-testid="holiday-enabled-switch" />
      </label>
      <div className={`mt-5 ${enabled ? "" : "opacity-60"}`} data-testid="holiday-theme-picker">
        <Label>Pick a holiday or season</Label>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {HOLIDAY_THEMES.map((t) => {
            const active = t.id === theme.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => patchSettings({ holidayTheme: t.id })}
                aria-pressed={active}
                data-testid={`holiday-theme-${t.id}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${active ? "border-lavender-700 bg-lavender-700 text-white" : "border-lavender-700/15 bg-white text-stone-700 hover:border-lavender-400"}`}
              >
                <span className="flex shrink-0 -space-x-1">
                  {t.icons.slice(0, 2).map((ic, i) => (
                    <HolidayIcon key={i} name={ic} color={active ? "#fff" : t.colors[i % t.colors.length]} size={18} />
                  ))}
                </span>
                <span className="flex-1 truncate font-semibold">{t.name}</span>
                <span className="flex shrink-0 gap-1">
                  {t.colors.slice(0, 3).map((c) => <span key={c} className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c }} />)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-5">
        <Label>Banner greeting</Label>
        <Input
          className="mt-1.5"
          value={settings.holidayGreeting || ""}
          onChange={(e) => patchSettings({ holidayGreeting: e.target.value })}
          placeholder={theme.greeting}
          data-testid="holiday-greeting-input"
        />
        <p className="mt-1 text-xs text-stone-400">Leave blank to use the suggested greeting for this theme.</p>
      </div>
      <div className="mt-4 overflow-hidden rounded-xl border border-lavender-700/10" data-testid="holiday-preview">
        <div className="flex items-center justify-center gap-3 px-4 py-2.5 text-white" style={{ background: `linear-gradient(90deg, ${theme.colors[0]}, ${theme.colors[1]}, ${theme.colors[0]})` }}>
          <HolidayIcon name={theme.icons[0]} color="rgba(255,255,255,.9)" size={18} />
          <span className="truncate font-serif text-sm italic" data-testid="holiday-preview-greeting">{holidayGreeting(settings, theme)}</span>
          <HolidayIcon name={theme.icons[1] || theme.icons[0]} color="rgba(255,255,255,.9)" size={18} />
        </div>
        <div className="flex items-center justify-between gap-3 bg-white px-4 py-2 text-xs text-stone-500">
          <span>Decorations:</span>
          <span className="flex gap-2">{theme.icons.map((ic, i) => <HolidayIcon key={i} name={ic} color={theme.colors[i % theme.colors.length]} size={20} />)}</span>
          <span data-testid="holiday-preview-frame">Photo frame: {FRAME_LABELS[theme.frame]}</span>
          <span className="flex items-center gap-1.5">Paws <span className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ backgroundColor: theme.pawColor }} /></span>
        </div>
      </div>
    </Card>
  );
}
